import React from "react";
import { useClient } from "sanity";
import { DashboardWidgetContainer } from "@sanity/dashboard";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { Label } from "@sanity/ui";
import { PreviewNotice, previewRows } from "../commons/previewRows";

const ValueLabel = styled(Label)`
  color: var(--card-muted-fg-color);
  text-align: left;
  white-space: wrap;
  font-weight: 400;
`;

const containerStyle = { margin: "10px" };

type Row = { name: string; code: string; reason?: string };

export type DocumentListExporterProps<T> = {
  query: string;
  referencedIdsQuery?: string;
  title: string;
  mapResult: (data: T) => Row;
};

export const DocumentListExporter = <T,>({
  query,
  referencedIdsQuery,
  mapResult,
  title
}: DocumentListExporterProps<T>) => {
  const client = useClient({ apiVersion: "2021-10-21" });

  const result = useQuery({
    queryKey: ["document-list-exporter", query],
    queryFn: async () => client.fetch<T[]>(query),
    enabled: !!query,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const referencedIdsResult = useQuery({
    queryKey: ["document-list-exporter-referenced-ids", referencedIdsQuery],
    queryFn: async () => client.fetch<string[]>(referencedIdsQuery as string),
    enabled: !!referencedIdsQuery,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isPending =
    result.isPending ||
    (!!referencedIdsQuery && referencedIdsResult.isPending);
  const hasError =
    result.status === "error" ||
    (!!referencedIdsQuery && referencedIdsResult.status === "error");
  const isReady =
    result.status === "success" &&
    (!referencedIdsQuery || referencedIdsResult.status === "success");

  const rows = React.useMemo(() => {
    if (!isReady) return [];
    const data = result.data ?? [];
    if (!referencedIdsQuery) return data.map(mapResult);

    const referencedIds = new Set(referencedIdsResult.data ?? []);
    return data
      .filter((item) =>
        referencedIds.has((item as unknown as { _id: string })._id)
      )
      .map(mapResult);
  }, [
    isReady,
    result.data,
    referencedIdsResult.data,
    referencedIdsQuery,
    mapResult
  ]);

  return (
    <DashboardWidgetContainer header={title}>
      {isPending && <div style={containerStyle}>Loading...</div>}
      {hasError && (
        <div style={{ ...containerStyle, color: "yellow" }}>
          Error loading data. Try to refresh the page or contact dev team
        </div>
      )}
      {isReady && <LoadedListExporter rows={rows} title={title} />}
    </DashboardWidgetContainer>
  );
};

type LoadedListExporterProps = { rows: Row[]; title: string };
const LoadedListExporter: React.FC<LoadedListExporterProps> = ({
  rows,
  title
}) => {
  const handleExportCSV = () => {
    if (rows.length === 0) return;

    const headers = ["Name", "Code", "Reason"];
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        [
          `${(row.name || "").replace(/"/g, '""')}`,
          `${(row.code || "").replace(/"/g, '""')}`,
          `${(row.reason || "").replace(/"/g, '""')}`
        ].join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <button
        onClick={handleExportCSV}
        disabled={rows.length === 0}
        style={{
          margin: "10px",
          padding: "6px",
          backgroundColor: "#2276fc",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: rows.length > 0 ? "pointer" : "not-allowed",
          opacity: rows.length > 0 ? 1 : 0.5
        }}
      >
        Export CSV ({rows.length} elements)
      </button>
      <table style={{ borderCollapse: "separate", borderSpacing: "21px" }}>
        <thead>
          <tr style={{ textAlign: "left" }}>
            <th>Name</th>
            <th>Code</th>
            <th>Issue</th>
          </tr>
        </thead>
        <tbody>
          {previewRows(rows).map((row) => (
            <tr key={row.code}>
              <td>
                <ValueLabel muted>{row.name}</ValueLabel>
              </td>
              <td>{row.code}</td>
              <td>{row.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <PreviewNotice total={rows.length} />
    </>
  );
};
